"use client"

import * as React from "react"
import type { ReactNode } from "react"

import { useActiveWorkspaceSlug } from "@/features/auth"
import { CreateKind, createKindListTarget } from "@/features/create-overlay/model/create-kind"
import { BranchDetailTab, buildBranchDetailTabHref } from "@/features/church/config/branches/model/branch-detail-tab"
import { HomeTile, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { BoardTileStates } from "@/features/finance/overview/board/board-tile-states"
import { usePeriodFallback } from "@/features/finance/overview/board/use-period-fallback"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import type { FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { ChurchActivitiesTile } from "@/features/finance/overview/church-f3/components/church-activities-tile"
import { ChurchBranchesTile } from "@/features/finance/overview/church-f3/components/church-branches-tile"
import { ChurchCashTile } from "@/features/finance/overview/church-f3/components/church-cash-tile"
import { ChurchRemittancesTile } from "@/features/finance/overview/church-f3/components/church-remittances-tile"
import { ChurchShareTile } from "@/features/finance/overview/church-f3/components/church-share-tile"
import { ChurchSilentTile } from "@/features/finance/overview/church-f3/components/church-silent-tile"
import { ChurchStrugglingTile } from "@/features/finance/overview/church-f3/components/church-struggling-tile"
import {
  ChurchTileCompactEmpty,
  ChurchTileWithheld,
  type TileFallback,
} from "@/features/finance/overview/church-f3/components/church-tile-parts"
import type { FallbackPeriod } from "@/features/finance/overview/church-f3/lib/fallback-period"
import { useChurchFinanceQuery } from "@/features/finance/overview/church-f3/hooks/use-church-finance-query"
import {
  activityBars,
  closestToDeficit,
  hasBranchMoney,
  remittanceRows,
  shareSegments,
  silentBranches,
  strugglingSummary,
} from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { FinanceWidgetId } from "@/features/finance/overview/lib/finance-widget-ids"
import { useChurchActivityRows } from "@/features/finance/overview/lib/use-church-activity-rows"
import { useGlobalBranchRows } from "@/features/finance/overview/lib/use-global-branch-rows"
import { useIsWithheld } from "@/features/finance/overview/widgets/widget-withheld-state"
import { useTranslations } from "@/i18n"
import { buildOrganizationUrl } from "@/shared/lib/branch/branch-admin-routing"

/**
 * The F3 tiles as dashboard widgets. Fixed heights make the engine's masonry rows line up like the design: the
 * first two rows share one height, the third is shorter. A tile resized wider keeps its height.
 */
const SHORT_ROW_TILES: ReadonlySet<FinanceWidgetId> = new Set([
  FinanceWidgetId.ChurchActivities,
  FinanceWidgetId.ChurchRemittances,
])

export const churchTileHeightClass = (id: FinanceWidgetId) => (SHORT_ROW_TILES.has(id) ? "h-[15rem]" : "h-[20rem]")

type WidgetProps = { context?: FinanceSurfaceContext }

/** The F3 tile states: the shared board state machine at this tile's row height. */
function TileStates(props: {
  id: FinanceWidgetId
  title: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  /** Nothing to show in this period nor the previous one: the tile sizes to its one line. */
  compact?: boolean
  children: () => ReactNode
}) {
  return <BoardTileStates {...props} testIdPrefix='church' heightClass={churchTileHeightClass(props.id)} />
}

/**
 * The previous comparable period an F3 tile falls back to when the board's period has no money yet, its subtitle
 * label, and the "record a transaction" action for a reader who may record in the parish.
 */
function useTileFallback(): { period: FallbackPeriod | null; fallback: TileFallback | null; record: HomeTileLink | null } {
  const t = useTranslations("finance.overview.church")
  const workspaceSlug = useActiveWorkspaceSlug()
  const { canEditParishFinance } = useFinancePageState()
  const { period, fallback } = usePeriodFallback()
  return React.useMemo(
    () => ({
      period,
      fallback,
      record:
        canEditParishFinance && workspaceSlug
          ? { label: t("compactEmpty.record"), href: buildOrganizationUrl(workspaceSlug, createKindListTarget(CreateKind.Offering)) }
          : null,
    }),
    [period, fallback, t, canEditParishFinance, workspaceSlug],
  )
}

/**
 * Branch rows for the board's period, or for the previous one when the board's carries no money yet. The fallback
 * read is the same endpoint, so it passes the same gate and disclosure.
 */
function useBranchRowsWithFallback() {
  const tile = useTileFallback()
  const current = useGlobalBranchRows()
  const currentEmpty = !current.isLoading && !current.isError && !hasBranchMoney(current.rows)
  const previous = useGlobalBranchRows({
    period: tile.period ?? undefined,
    enabled: currentEmpty && tile.period !== null,
  })
  const useFallback = currentEmpty && hasBranchMoney(previous.rows)
  return {
    tile,
    current,
    rows: useFallback ? previous : current,
    fallback: useFallback ? tile.fallback : null,
    bothEmpty: currentEmpty && !previous.isLoading && !hasBranchMoney(previous.rows),
    isLoading: current.isLoading || (currentEmpty && previous.isLoading),
  }
}

export function ChurchCashWidget() {
  const t = useTranslations("finance.overview")
  const query = useChurchFinanceQuery({ enabled: true })
  const title = t("widgets.churchCash.title")
  return (
    <TileStates
      id={FinanceWidgetId.ChurchCash}
      title={title}
      isLoading={query.isPending}
      isError={query.isError}
      onRetry={() => void query.refetch()}
    >
      {() =>
        query.data?.figures ? (
          <ChurchCashTile cash={query.data.figures.cash} currency={query.data.currency} />
        ) : (
          <HomeTile title={title}>
            <ChurchTileWithheld />
          </HomeTile>
        )
      }
    </TileStates>
  )
}

export function ChurchBranchesWidget() {
  const t = useTranslations("finance.overview")
  const workspaceSlug = useActiveWorkspaceSlug()
  const data = useBranchRowsWithFallback()
  const title = t("widgets.churchBranches.title")
  return (
    <TileStates
      id={FinanceWidgetId.ChurchBranches}
      title={title}
      isLoading={data.isLoading}
      isError={data.current.isError}
      onRetry={data.current.refetch}
      compact={data.bothEmpty}
    >
      {() =>
        data.bothEmpty ? (
          <ChurchTileCompactEmpty title={title} message={t("church.branches.empty")} action={data.tile.record} />
        ) : (
          <ChurchBranchesTile
            rows={data.rows.rows}
            currency={data.rows.currency}
            fallback={data.fallback}
            link={
              workspaceSlug ? { label: t("church.branches.link"), href: buildOrganizationUrl(workspaceSlug, "/branches") } : null
            }
          />
        )
      }
    </TileStates>
  )
}

export function ChurchShareWidget() {
  const t = useTranslations("finance.overview")
  const data = useBranchRowsWithFallback()
  const title = t("widgets.churchShare.title")
  return (
    <TileStates
      id={FinanceWidgetId.ChurchShare}
      title={title}
      isLoading={data.isLoading}
      isError={data.current.isError}
      onRetry={data.current.refetch}
      compact={data.bothEmpty}
    >
      {() =>
        data.bothEmpty ? (
          <ChurchTileCompactEmpty title={title} message={t("church.share.empty")} action={data.tile.record} />
        ) : (
          <ChurchShareTile
            fallback={data.fallback}
            segments={shareSegments(
              data.rows.rows,
              data.rows.totalIncome,
              data.rows.figuresAreRatios,
              t("church.share.others"),
            )}
          />
        )
      }
    </TileStates>
  )
}

export function ChurchStrugglingWidget() {
  const t = useTranslations("finance.overview")
  const workspaceSlug = useActiveWorkspaceSlug()
  const data = useBranchRowsWithFallback()
  // The streak and the monthly bars are extra: if they fail, the tile still names the branches in deficit. They
  // describe the months up to the board's period end, so they are only drawn for the board's own period.
  const church = useChurchFinanceQuery({ enabled: true })
  return (
    <TileStates
      id={FinanceWidgetId.ChurchStruggling}
      title={t("widgets.churchStruggling.title")}
      isLoading={data.isLoading || church.isPending}
      isError={data.current.isError}
      onRetry={data.current.refetch}
    >
      {() => {
        const rows = data.rows.rows
        const summary = strugglingSummary(rows, data.fallback ? null : (church.data?.figures?.branches ?? null))
        const detailHref =
          summary.worst && workspaceSlug && !summary.detail?.isParish && church.data?.figures?.branches.some(
            (branch) => branch.branchId === summary.worst?.id && !branch.isParish,
          )
            ? buildBranchDetailTabHref(summary.worst.id, BranchDetailTab.Finance, workspaceSlug)
            : null
        return (
          <ChurchStrugglingTile
            summary={summary}
            currency={data.rows.currency}
            detailHref={detailHref}
            closest={closestToDeficit(rows)}
            fallback={data.fallback}
          />
        )
      }}
    </TileStates>
  )
}

export function ChurchSilentWidget({ context }: WidgetProps) {
  const t = useTranslations("finance.overview")
  const workspaceSlug = useActiveWorkspaceSlug()
  const withheld = useIsWithheld(context)
  const branchRows = useGlobalBranchRows()
  // The last-entry dates come with the church figures, which a reader the absolutes are withheld from never asks for.
  const church = useChurchFinanceQuery({ enabled: !withheld })
  const figures = withheld ? null : (church.data?.figures ?? null)
  return (
    <TileStates
      id={FinanceWidgetId.ChurchSilent}
      title={t("widgets.churchSilent.title")}
      isLoading={branchRows.isLoading || (!withheld && church.isPending)}
      isError={branchRows.isError}
      onRetry={branchRows.refetch}
    >
      {() => (
        <ChurchSilentTile
          branches={silentBranches(branchRows.rows, figures?.branches ?? null, t("widgets.branchPerformance.parish"))}
          datesKnown={figures !== null}
          detailHrefOf={(branch) =>
            branch.isParish || !workspaceSlug || !figures
              ? null
              : buildBranchDetailTabHref(branch.id, BranchDetailTab.Overview, workspaceSlug)
          }
        />
      )}
    </TileStates>
  )
}

export function ChurchActivitiesWidget() {
  const t = useTranslations("finance.overview")
  const tile = useTileFallback()
  const current = useChurchActivityRows()
  const currentEmpty = !current.query.isLoading && !current.query.isError && (current.data?.rows.length ?? 0) === 0
  const previous = useChurchActivityRows({ period: tile.period ?? undefined, enabled: currentEmpty && tile.period !== null })
  const useFallback = currentEmpty && (previous.data?.rows.length ?? 0) > 0
  const bothEmpty = currentEmpty && !previous.query.isLoading && !useFallback
  const shown = useFallback ? previous.data : current.data
  const title = t("widgets.churchActivities.title")
  return (
    <TileStates
      id={FinanceWidgetId.ChurchActivities}
      title={title}
      isLoading={current.query.isLoading || (currentEmpty && previous.query.isLoading)}
      isError={current.query.isError}
      onRetry={() => void current.query.refetch()}
      compact={bothEmpty}
    >
      {() =>
        bothEmpty ? (
          <ChurchTileCompactEmpty title={title} message={t("church.activities.empty")} action={tile.record} />
        ) : (
          <ChurchActivitiesTile
            rows={activityBars(shown?.rows ?? [])}
            currency={shown?.currency ?? ""}
            fallback={useFallback ? tile.fallback : null}
          />
        )
      }
    </TileStates>
  )
}

export function ChurchRemittancesWidget() {
  const t = useTranslations("finance.overview")
  const tile = useTileFallback()
  const query = useChurchFinanceQuery({ enabled: true })
  const currentRows = query.data?.figures ? remittanceRows(query.data.figures.branches) : null
  const currentEmpty = currentRows !== null && !hasRemittedNow(currentRows)
  // The fallback asks the same endpoint for the previous period: same gate, same all-or-nothing disclosure.
  const previous = useChurchFinanceQuery({
    enabled: currentEmpty && tile.period !== null,
    period: tile.period ?? undefined,
  })
  const previousRows = previous.data?.figures ? remittanceRows(previous.data.figures.branches) : null
  const useFallback = currentEmpty && previousRows !== null && hasRemittedNow(previousRows)
  const bothEmpty = currentEmpty && !previous.isPending && !useFallback
  const title = t("widgets.churchRemittances.title")
  return (
    <TileStates
      id={FinanceWidgetId.ChurchRemittances}
      title={title}
      isLoading={query.isPending || (currentEmpty && tile.period !== null && previous.isPending)}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      compact={bothEmpty}
    >
      {() =>
        !query.data?.figures || !currentRows ? (
          <HomeTile title={title}>
            <ChurchTileWithheld />
          </HomeTile>
        ) : bothEmpty ? (
          <ChurchTileCompactEmpty title={title} message={t("church.remittances.empty")} action={null} />
        ) : (
          <ChurchRemittancesTile
            rows={useFallback && previousRows ? previousRows : currentRows}
            currency={query.data.currency}
            fallback={useFallback ? tile.fallback : null}
          />
        )
      }
    </TileStates>
  )
}

const hasRemittedNow = (rows: ReturnType<typeof remittanceRows>) => rows.some((row) => Number(row.remitted) > 0)
